import { useState, useEffect } from 'react'
import { customersAPI } from '../../services/api'

import { logger } from '@/utils/logger'

export default function CustomersManagement() {
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    company_name: '',
    contact_person: '',
    phone: '',
    street: '',
    zip_code: '',
    city: '',
    country: 'DE',
    is_active: true,
  })

  useEffect(() => {
    loadCustomers()
  }, [])

  const loadCustomers = async () => {
    setLoading(true)
    try {
      const response = await customersAPI.getAll()
      setCustomers(response.data)
    } catch (err) {
      setError('Fehler beim Laden der Kunden')
      logger.error('Error loading customers:', err)
    } finally {
      setLoading(false)
    }
  }

  const emptyFormData = {
    email: '',
    username: '',
    password: '',
    company_name: '',
    contact_person: '',
    phone: '',
    street: '',
    zip_code: '',
    city: '',
    country: 'DE',
    is_active: true,
  }

  const handleOpenModal = (customer = null) => {
    if (customer) {
      setEditingCustomer(customer)
      setFormData({
        email: customer.email,
        username: customer.username,
        password: '',
        company_name: customer.company_name || '',
        contact_person: customer.contact_person || '',
        phone: customer.phone || '',
        street: customer.street || '',
        zip_code: customer.zip_code || '',
        city: customer.city || '',
        country: customer.country || 'DE',
        is_active: customer.is_active,
      })
    } else {
      setEditingCustomer(null)
      setFormData(emptyFormData)
    }
    setShowModal(true)
    setError('')
    setSuccess('')
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingCustomer(null)
    setFormData(emptyFormData)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    try {
      if (editingCustomer) {
        const updateData = { ...formData }
        if (!updateData.password) {
          delete updateData.password
        }
        await customersAPI.update(editingCustomer.id, updateData)
        setSuccess('Kunde erfolgreich aktualisiert')
      } else {
        if (!formData.password) {
          setError('Passwort ist erforderlich für neue Kunden')
          return
        }
        await customersAPI.create(formData)
        setSuccess('Kunde erfolgreich erstellt')
      }

      await loadCustomers()
      handleCloseModal()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern des Kunden')
      logger.error('Error saving customer:', err)
    }
  }

  const handleDelete = async (customer) => {
    if (!confirm(`Möchten Sie den Kunden "${customer.company_name || customer.username}" wirklich löschen?`)) {
      return
    }

    try {
      await customersAPI.delete(customer.id)
      setSuccess('Kunde erfolgreich gelöscht')
      await loadCustomers()
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Löschen des Kunden')
      logger.error('Error deleting customer:', err)
      setTimeout(() => setError(''), 3000)
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
        <div className="wc-spinner" />
      </div>
    )
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Kundenverwaltung</h1>
        <button onClick={() => handleOpenModal()} className="wc-btn wc-btn-primary">
          + Neuer Kunde
        </button>
      </div>

      {error && <div className="wc-alert wc-alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="wc-alert wc-alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      <div className="wc-card wc-elev-sm" style={{ padding: 0 }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="wc-table">
            <thead>
              <tr>
                <th>Firma / Name</th>
                <th>Kontakt</th>
                <th>E-Mail</th>
                <th>Telefon</th>
                <th>Status</th>
                <th>Erstellt</th>
                <th style={{ textAlign: 'right' }}>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '48px 0' }} className="wc-muted">
                    Keine Kunden vorhanden
                  </td>
                </tr>
              ) : (
                customers.map((customer) => (
                  <tr key={customer.id}>
                    <td>
                      <div>{customer.company_name || '-'}</div>
                      <div className="wc-muted" style={{ fontSize: 12 }}>{customer.username}</div>
                    </td>
                    <td>{customer.contact_person || '-'}</td>
                    <td>{customer.email}</td>
                    <td>{customer.phone || '-'}</td>
                    <td>
                      <span className={`wc-tag ${customer.is_active ? 'wc-tag-accent' : 'wc-tag-neutral'}`}>
                        {customer.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="wc-muted">{new Date(customer.created_at).toLocaleDateString('de-DE')}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button onClick={() => handleOpenModal(customer)} className="wc-btn wc-btn-ghost" style={{ padding: '4px 8px' }}>
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(customer)}
                        className="wc-btn wc-btn-ghost"
                        style={{ padding: '4px 8px', color: '#fca5a5' }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="wc-modal-overlay" onClick={handleCloseModal}>
          <div className="wc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="wc-card-title" style={{ margin: 0 }}>
              {editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
            </h3>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                <div className="wc-field">
                  <label>E-Mail *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="wc-input"
                    required
                  />
                </div>

                <div className="wc-field">
                  <label>Benutzername *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="wc-input"
                    required
                  />
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>Passwort {editingCustomer ? '(leer lassen für keine Änderung)' : '*'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="wc-input"
                    required={!editingCustomer}
                    minLength={8}
                    placeholder={editingCustomer ? 'Neues Passwort (optional)' : ''}
                  />
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>Firmenname</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Ansprechpartner</label>
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field">
                  <label>Telefon</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="wc-input"
                  />
                </div>

                <div className="wc-field" style={{ gridColumn: 'span 2' }}>
                  <label>Straße &amp; Hausnummer</label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="wc-input"
                    placeholder="Hauptstraße 12"
                  />
                </div>

                <div className="wc-field">
                  <label>PLZ</label>
                  <input
                    type="text"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                    className="wc-input"
                    placeholder="12345"
                  />
                </div>

                <div className="wc-field">
                  <label>Stadt</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="wc-input"
                    placeholder="München"
                  />
                </div>

                <div className="wc-field">
                  <label>Land</label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="wc-input"
                    maxLength={2}
                    placeholder="DE"
                  />
                </div>

                <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center' }}>
                  <label className="wc-check">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Aktiv
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 4 }}>
                <button type="button" onClick={handleCloseModal} className="wc-btn wc-btn-secondary">
                  Abbrechen
                </button>
                <button type="submit" className="wc-btn wc-btn-primary">
                  {editingCustomer ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
